/**
 * Cache Manager
 *
 * Manages application cache clearing based on version changes.
 * Preserves authentication tokens and user preferences while clearing
 * other cached data when the app version is updated.
 */

// Keys that should be preserved during cache clearing
const PRESERVED_KEYS = [
  'auth_token',        // Authentication token
  'selectedCurrency',  // User's currency preference
  'selectedLocale',    // User's language preference
  'cookieConsent',     // GDPR cookie consent status
];

// Key used to store the app version in localStorage
const VERSION_KEY = 'app_version';

/**
 * Gets the current application version from package.json
 */
export const getCurrentAppVersion = (): string => {
  // Version is injected at build time by Vite
  return import.meta.env.VITE_APP_VERSION || '0.0.0';
};

/**
 * Gets the stored app version from localStorage
 */
export const getStoredVersion = (): string | null => {
  return localStorage.getItem(VERSION_KEY);
};

/**
 * Updates the stored app version in localStorage
 */
export const updateStoredVersion = (version: string): void => {
  localStorage.setItem(VERSION_KEY, version);
};

/**
 * Clears all localStorage items except preserved keys
 */
export const clearCacheExceptPreserved = (): void => {
  // Store preserved values
  const preservedValues: Record<string, string> = {};

  PRESERVED_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      preservedValues[key] = value;
    }
  });

  // Clear all localStorage
  localStorage.clear();

  // Restore preserved values
  Object.entries(preservedValues).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });

  console.log('Cache cleared. Preserved keys:', Object.keys(preservedValues));
};

/**
 * Checks if the app version has changed and clears cache if needed
 * Should be called when the application starts
 */
export const checkAndClearCache = (): void => {
  const currentVersion = getCurrentAppVersion();
  const storedVersion = getStoredVersion();

  // If this is the first run or version has changed
  if (!storedVersion || storedVersion !== currentVersion) {
    console.log(
      `App version changed: ${storedVersion || 'none'} -> ${currentVersion}. Clearing cache...`
    );

    clearCacheExceptPreserved();
    updateStoredVersion(currentVersion);
  } else {
    console.log(`App version unchanged: ${currentVersion}`);
  }
};

/**
 * Manually clear cache (useful for testing or admin purposes)
 */
export const manualClearCache = (): void => {
  clearCacheExceptPreserved();
  console.log('Cache manually cleared');
};
